import { canonical, legalBridges } from "./graph";
import {
  MAX_SIZE,
  MIN_SIZE,
  WATER,
  type Bridge,
  type BridgesDefinition,
  type BridgesProgress,
  type BridgesSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Join the islands with bridges. A bridge runs straight across the water between two islands on the same row or column, never crosses another bridge nor an island, and two islands share at most two bridges. The number on an island tells how many bridges leave it, and every island must end up connected to all the others.",
  fr: "Reliez les îles par des ponts. Un pont traverse l'eau en ligne droite entre deux îles d'une même ligne ou d'une même colonne, sans croiser d'autre pont ni d'île, et deux îles partagent au plus deux ponts. Le nombre d'une île indique combien de ponts en partent, et toutes les îles doivent finir reliées entre elles.",
};

export function emptyDefinition(): BridgesDefinition {
  return {
    puzzleType: "bridges",
    typeVersion: 1,
    rows: 7,
    cols: 7,
    islands: Array.from({ length: 7 }, () => WATER.repeat(7)),
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return MIN_SIZE;
  return Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.trunc(value)));
}

/** Crops or pads an island grid to the given size, keeping the top-left corner. */
export function resizeIslands(islands: string[], rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, i) => {
    const line = islands[i] ?? "";
    return line.length >= cols ? line.slice(0, cols) : line + WATER.repeat(cols - line.length);
  });
}

/** The definition's grid, at its own size whatever was stored. */
export function gridOf(definition: BridgesDefinition): string[] {
  return resizeIslands(definition.islands, definition.rows, definition.cols);
}

export function emptyProgress(_definition: BridgesDefinition): BridgesProgress {
  return { version: 1, bridges: [] };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(definition: BridgesDefinition, raw: unknown): BridgesProgress {
  const list =
    typeof raw === "object" && raw !== null ? (raw as { bridges?: unknown }).bridges : undefined;
  return { version: 1, bridges: canonical(legalBridges(gridOf(definition), list)) };
}

/** The bridges in their canonical order: the same board always submits the same bytes. */
export function serializeSubmission(
  definition: BridgesDefinition,
  progress: BridgesProgress,
): BridgesSubmission {
  return { bridges: canonical(legalBridges(gridOf(definition), progress.bridges)) };
}

export function bridgesSignature(bridges: Bridge[]): string {
  return canonical(bridges)
    .map(({ a, b, n }) => `${a[0]},${a[1]}-${b[0]},${b[1]}x${n}`)
    .join(";");
}
